import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Loader2,
  Trash2,
  Pencil,
  TrendingDown,
  TrendingUp,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });
const formatFt = (n) => (n == null ? "—" : `${huFmt.format(n)} Ft`);
const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`} {...rest}>{children}</td>
);

export default function SupplierProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [prices, setPrices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: pr }, { data: sp }] = await Promise.all([
        api.get(`/supplier-products/${id}`),
        api.get(`/supplier-products/${id}/prices`),
        api.get("/partners?type=supplier"),
      ]);
      setProduct(p);
      setPrices(pr);
      setSuppliers(sp);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const cheapestId = prices.length ? prices[0]._id : null;
  const cheapest = prices[0];
  const highest = prices[prices.length - 1];
  const spread = cheapest && highest ? highest.net_price - cheapest.net_price : 0;
  const spreadPct = cheapest && cheapest.net_price > 0 ? (spread / cheapest.net_price) * 100 : 0;

  const onDeletePrice = async (p) => {
    if (!window.confirm(`Törlöd ezt az árat (${p.supplier_name})?`)) return;
    try {
      await api.delete(`/supplier-prices/${p._id}`);
      setPrices((prev) => prev.filter((x) => x._id !== p._id));
      toast.success("Ár törölve.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  if (loading || !product) {
    return (
      <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <div>
        <button
          onClick={() => navigate("/supplier-products")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Beszállítói termékek
        </button>
      </div>

      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <Package className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">Termék</span>
          </div>
          <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">{product.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {[product.category, product.unit && `egység: ${product.unit}`, product.sku && `SKU: ${product.sku}`]
              .filter(Boolean).join(" · ") || "—"}
          </p>
          {product.description && (
            <p className="mt-1 text-xs text-muted-foreground">{product.description}</p>
          )}
        </div>
      </header>

      {prices.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Legolcsóbb (nettó)" tone="emerald" icon={<TrendingDown className="h-3.5 w-3.5" />}
            value={formatFt(cheapest.net_price)} sub={cheapest.supplier_name} />
          <Stat label="Legdrágább (nettó)" tone="rose" icon={<TrendingUp className="h-3.5 w-3.5" />}
            value={formatFt(highest.net_price)} sub={highest.supplier_name} />
          <Stat label="Ársáv" tone="muted"
            value={spread > 0 ? `+${formatFt(spread)}` : "—"}
            sub={spread > 0 ? `${spreadPct.toFixed(1)}% különbség` : "azonos árak"} />
        </div>
      )}

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <header className="flex flex-col gap-1 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Beszállítói árak
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {prices.length === 0 ? "Még nincs ár rögzítve." : `${prices.length} ár · legolcsóbb kiemelve`}
            </p>
          </div>
          <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-price-button">
            <Plus className="h-4 w-4" /> Új ár
          </Button>
        </header>

        {prices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <p className="text-sm font-medium">Nincs rögzített beszállítói ár</p>
            <p className="text-xs text-muted-foreground">Add hozzá az első beszállítói árajánlatot.</p>
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
              <Plus className="h-3.5 w-3.5" /> Új ár
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Beszállító</Th>
                  <Th className="text-right">Nettó ár</Th>
                  <Th className="text-right">ÁFA</Th>
                  <Th className="text-right">Bruttó ár</Th>
                  <Th>Megjegyzés</Th>
                  <Th>Rögzítette</Th>
                  <Th className="pr-4 text-right">Művelet</Th>
                </tr>
              </thead>
              <tbody>
                {prices.map((p) => {
                  const isCheap = p._id === cheapestId;
                  return (
                    <tr key={p._id} data-testid={`price-row-${p._id}`}
                      className={`border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025] ${isCheap ? "bg-emerald-500/[0.04]" : ""}`}>
                      <Td>
                        <span className="font-medium">{p.supplier_name}</span>
                        {isCheap && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700 dark:text-emerald-300">
                            <TrendingDown className="h-2.5 w-2.5" /> Legolcsóbb
                          </span>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums font-medium">{formatFt(p.net_price)}</Td>
                      <Td className="text-right tabular-nums text-muted-foreground">{p.vat_rate}%</Td>
                      <Td className="text-right tabular-nums">{formatFt(p.gross_price)}</Td>
                      <Td className="text-muted-foreground">{p.notes || "—"}</Td>
                      <Td className="text-muted-foreground text-xs">{p.created_by_name || "—"}</Td>
                      <Td className="pr-4 text-right">
                        <div className="ml-auto flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditing(p)}
                            aria-label="Szerkesztés"
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => onDeletePrice(p)}
                            aria-label="Törlés"
                            data-testid={`delete-price-${p._id}`}
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <PriceFormDialog
        open={openCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setOpenCreate(false); setEditing(null); } }}
        product={product}
        price={editing}
        suppliers={suppliers}
        onSaved={() => loadAll()}
      />
    </div>
  );
}

function Stat({ label, value, sub, tone, icon }) {
  const toneClass =
    tone === "emerald"
      ? "border-emerald-500/30 bg-emerald-500/[0.04] text-emerald-700 dark:text-emerald-300"
      : tone === "rose"
        ? "border-rose-500/30 bg-rose-500/[0.04] text-rose-700 dark:text-rose-300"
        : "border-border bg-muted/30 text-foreground";
  return (
    <div className={`rounded-xl border px-4 py-3 ${toneClass}`}>
      <p className="flex items-center gap-1 text-[10.5px] font-medium uppercase tracking-[0.14em] opacity-80">
        {icon} {label}
      </p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs opacity-80">{sub}</p>}
    </div>
  );
}

function PriceFormDialog({ open, onOpenChange, product, price, suppliers, onSaved }) {
  const [form, setForm] = useState({ supplier_id: "", net_price: "", vat_rate: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!price;

  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm({
      supplier_id: price?.supplier_id || "",
      net_price: price?.net_price?.toString() || "",
      vat_rate: price?.vat_rate?.toString() || "",
      notes: price?.notes || "",
    });
  }, [open, price]);

  const net = parseFloat(form.net_price);
  const vat = parseFloat(form.vat_rate);
  const gross = Number.isFinite(net) && Number.isFinite(vat)
    ? Math.round(net * (1 + vat / 100) * 100) / 100
    : null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.supplier_id) return setErr("Beszállító kiválasztása kötelező.");
    if (!Number.isFinite(net) || net < 0) return setErr("Érvénytelen nettó ár.");
    if (!Number.isFinite(vat) || vat < 0 || vat > 100) return setErr("Érvénytelen ÁFA.");
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        product_id: product._id,
        net_price: net,
        vat_rate: vat,
        notes: form.notes.trim() || null,
      };
      isEdit
        ? await api.patch(`/supplier-prices/${price._id}`, payload)
        : await api.post("/supplier-prices", payload);
      toast.success(isEdit ? "Ár frissítve." : "Ár rögzítve.");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="price-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Ár szerkesztése" : "Új beszállítói ár"}</DialogTitle>
          <DialogDescription>
            Termék: <b className="text-foreground">{product.name}</b>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block space-y-1.5">
            <span className="block text-xs font-medium text-muted-foreground">Beszállító *</span>
            <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
              <SelectTrigger data-testid="price-supplier-trigger"><SelectValue placeholder="Válassz beszállítót…" /></SelectTrigger>
              <SelectContent>
                {suppliers.length === 0 ? (
                  <SelectItem value="__no__" disabled>Nincs beszállító — hozz létre egyet</SelectItem>
                ) : (
                  suppliers.map((s) => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)
                )}
              </SelectContent>
            </Select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1.5">
              <span className="block text-xs font-medium text-muted-foreground">Nettó ár (Ft) *</span>
              <Input
                type="number" min="0" step="0.01" inputMode="decimal"
                value={form.net_price}
                onChange={(e) => setForm((f) => ({ ...f, net_price: e.target.value }))}
                data-testid="price-net"
                placeholder="pl. 295"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="block text-xs font-medium text-muted-foreground">ÁFA (%) *</span>
              <Select value={form.vat_rate} onValueChange={(v) => setForm((f) => ({ ...f, vat_rate: v }))}>
                <SelectTrigger data-testid="price-vat-trigger"><SelectValue placeholder="Válassz…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                  <SelectItem value="27">27%</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <p className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Bruttó ár (számított)</span>
              <span className="tabular-nums font-semibold" data-testid="price-gross-preview">
                {gross != null ? formatFt(gross) : "—"}
              </span>
            </p>
          </div>
          <label className="block space-y-1.5">
            <span className="block text-xs font-medium text-muted-foreground">Megjegyzés</span>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="pl. mennyiségi kedvezmény 100 db felett" />
          </label>
          {err && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="price-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Mentés" : "Rögzítés"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
